import { Box, Menu, MenuItem, Tooltip, Typography, alpha } from "@mui/material";
import { ENTER_TOUCH_DELAY, LEAVE_TOUCH_DELAY } from "./LightTooltip";
import {
  HCWMessageData,
  MessageDeliveryStatus,
  MessageType,
  MessageVisibility,
  Role,
} from "../features/data/patientApiSlice";
import { useCallback, useEffect, useState } from "react";

import CheckboxMenuItem from "./CheckboxMenuItem";
import ClinicianBadge from "./ClinicianBadge";
import IconButton from "./IconButton";
import MessageHiddenIcon from "@mui/icons-material/VisibilityOff";
import MessageVisibleIcon from "@mui/icons-material/Visibility";
import Photo from "./Photo";
import RadioMenuItem from "./RadioMenuItem";
import { combineSx, createStyleSheet } from "../utils/styles";
import { formatDateTime } from "../utils/dates";
import { formatDistanceToNow } from "date-fns";
import { getTemplateInterpolation } from "../utils/strings";
import { templateTitleOverrides } from "../utils/nudgeTemplateData";
import { theme } from "../../theme";
import { useTranslation } from "react-i18next";

const ChatMessageEntry = (props: {
  message: HCWMessageData;
  patientName: string;
  patientGroup: string;
  onMessageVisibilityChange: (
    messageUid: string,
    visibility: MessageVisibility
  ) => void;
  chatOpen: boolean;
}) => {
  const { message, patientName, patientGroup, onMessageVisibilityChange, chatOpen } = props;
  const { t } = useTranslation("ChatUx");
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [isPeekingFromHidden, setIsPeekingFromHidden] = useState(false);

  const displayName =
    message.role === Role.hcw
      ? message.hcw?.name || t("common:unknown")
      : message.role === Role.assistant
      ? t("common:systemResponse")
      : patientName;
  const reactions = message.reactions.reduce(
    (acc, reaction) =>
      Object.assign(acc, {
        [reaction.emoji]: (acc[reaction.emoji] || 0) + 1,
      }),
    {} as { [key: string]: number }
  );
  const sentTemplateMessage = t("sentTemplateMessage");
  const contentToRender =
    message.type === MessageType.template &&
    message.text.startsWith(sentTemplateMessage) ? (
      <>
        <Typography variant="body2" sx={styles.templateMessageText}>
          {t("sentNudge")}
        </Typography>
        <Typography variant="body2bold" sx={styles.templateMessageText}>
          {t(
            "ChatUx:templateTitles:" +
              (templateTitleOverrides[
                message.text.substring(sentTemplateMessage.length)
              ] || message.text.substring(sentTemplateMessage.length))
          )}
          :&nbsp;
        </Typography>
        <Typography
          variant="body2"
          sx={combineSx(styles.templateMessageText, styles.hiddenMessageText)}
          dangerouslySetInnerHTML={{
            __html: t(
              "ChatUx:templates:" +
                message.text.substring(sentTemplateMessage.length),
              getTemplateInterpolation(
                patientGroup,
                message.text.substring(sentTemplateMessage.length)
              )
            ),
          }}
        />
      </>
    ) : !isPeekingFromHidden &&
      message.visibility === MessageVisibility.hidden_appropriate ? (
      <Typography variant="body2" sx={styles.hiddenMessageText}>
        {t("messageHiddenAppropriate")}
      </Typography>
    ) : !isPeekingFromHidden &&
      message.visibility === MessageVisibility.hidden_inappropriate ? (
      <Typography variant="body2" sx={styles.hiddenMessageText}>
        {t("messageHiddenInappropriate")}
      </Typography>
    ) : message.type === MessageType.text ? (
      <Typography variant="body2">{message.text}</Typography>
    ) : message.type === MessageType.image ? (
      <>
        {message.attachment ? (
          <Photo
            url={message.attachment.url}
            aspectRatio="auto"
            sx={styles.messageImageContent}
          />
        ) : null}
        {message.text ? (
          <Typography variant="body2">{message.text}</Typography>
        ) : null}
      </>
    ) : message.type === MessageType.audio ? (
      <>
        {message.attachment ? (
          <Box
            id={message.message_id + "_audio"}
            component="audio"
            controls
            src={message.attachment.url}
            sx={styles.messageAudioContent}
          />
        ) : null}
        {message.text ? (
          <Typography variant="body2">{message.text}</Typography>
        ) : null}
      </>
    ) : message.type === MessageType.video ? (
      <>
        {message.attachment ? (
          <Box
            component="video"
            controls
            src={message.attachment.url}
            sx={styles.messageVideoContent}
          />
        ) : null}
        {message.text ? (
          <Typography variant="body2">{message.text}</Typography>
        ) : null}
      </>
    ) : (
      <>
        <Typography variant="body2">
          {t("unknownMessageType", { type: message.type })}
        </Typography>
        {message.text ? (
          <Typography variant="body2">{message.text}</Typography>
        ) : null}
      </>
    );

  // Fix bug with Chrome where audio elements inside a narrow container don't
  // resize properly when the container is resized.
  useEffect(() => {
    if (chatOpen) {
      const audioElement = document.getElementById(
        message.message_id + "_audio"
      ) as HTMLAudioElement;
      if (audioElement) {
        setTimeout(() => {
          audioElement.style.alignSelf = "flex-start";
        }, theme.transitions.duration.shorter);
      }
    }
  }, [chatOpen, message.message_id]);

  const handleMessageVisibilityMenuOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      setMenuAnchor(e.currentTarget);
    },
    []
  );
  const handleMessageVisibilityMenuClose = useCallback((e: React.SyntheticEvent) => {
    setMenuAnchor(null);
    e.stopPropagation();
  }, []);

  const handleMessageVisibilityChange = useCallback(
    (visibility: MessageVisibility) => () => {
      setMenuAnchor(null);
      onMessageVisibilityChange(message.message_id, visibility);
      setIsPeekingFromHidden(false);
    },
    [message.message_id, onMessageVisibilityChange]
  );
  const handleTogglePeekFromHidden = useCallback(() => {
    setMenuAnchor(null);
    setIsPeekingFromHidden(!isPeekingFromHidden);
  }, [isPeekingFromHidden]);

  return (
    <Box sx={styles.messageEntry}>
      <Box sx={styles.messageHeader}>
        <ClinicianBadge
          clinician={{ name: displayName, picture: message.hcw?.picture }}
          sx={{ marginRight: 1 }}
        />
        <Typography variant="h6" sx={styles.displayName}>
          {displayName}
        </Typography>
        <Tooltip
          title={formatDateTime(new Date(message.sent_ts))}
          enterTouchDelay={ENTER_TOUCH_DELAY}
          leaveTouchDelay={LEAVE_TOUCH_DELAY}
          onContextMenu={(event) => event.preventDefault()}
        >
          <Typography variant="detailCellSubtitle">
            {formatDistanceToNow(message.sent_ts, { addSuffix: true })}
          </Typography>
        </Tooltip>
        {message.role === Role.user ? (
          <>
            <IconButton
              icon={
                message.visibility === MessageVisibility.hidden_appropriate ||
                message.visibility ===
                  MessageVisibility.hidden_inappropriate ? (
                  <MessageHiddenIcon fontSize="small" />
                ) : (
                  <MessageVisibleIcon fontSize="small" />
                )
              }
              tooltip={t("messageVisibility")}
              onClick={handleMessageVisibilityMenuOpen}
              size="small"
              sx={styles.messageVisibilityButton}
            />
            <Menu
              anchorEl={menuAnchor}
              open={menuAnchor != null}
              onClose={handleMessageVisibilityMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              slotProps={{
                list: {
                  role: "listbox",
                },
              }}
            >
              {[
                {
                  visibility: MessageVisibility.visible,
                  label: "messageVisible",
                },
                {
                  visibility: MessageVisibility.hidden_appropriate,
                  label: "messageHiddenAppropriate",
                },
                {
                  visibility: MessageVisibility.hidden_inappropriate,
                  label: "messageHiddenInappropriate",
                },
              ].map((item) => (
                <RadioMenuItem
                  key={item.visibility}
                  value={item.visibility}
                  label={t(item.label)}
                  selectedValue={message.visibility}
                  onChange={handleMessageVisibilityChange(item.visibility)}
                />
              ))}
              <MenuItem divider disabled />
              <CheckboxMenuItem
                disabled={message.visibility === MessageVisibility.visible}
                value={"peekFromHidden"}
                label={t("peekFromHidden")}
                selectedValues={
                  isPeekingFromHidden ? ["peekFromHidden"] : undefined
                }
                onChange={handleTogglePeekFromHidden}
              />
            </Menu>
          </>
        ) : null}
      </Box>
      <Box sx={styles.messageContent}>{contentToRender}</Box>
      {message.delivery_status === MessageDeliveryStatus.failed && (
        <Box sx={styles.deliveryFailure}>
          {t("ChatUx:messageDeliveryFailed")}
        </Box>
      )}
      {Object.keys(reactions).length > 0 ? (
        <Box sx={styles.reactionContainer}>
          {Object.keys(reactions).map((key) => (
            <Box key={key} sx={styles.reactionElement}>
              <Typography variant="body2">
                {reactions[key] > 1 ? `${key} ${reactions[key]}` : key}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Box>
  );
};

const styles = createStyleSheet({
  messageEntry: {
    display: "flex",
    flexDirection: "column",
    p: 1,
  },
  messageHeader: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
  displayName: {
    flex: 1,
    [theme.breakpoints.down("desktop")]: {
      flex: 0.9,
    },
  },
  messageVisibilityButton: {
    color: (theme) => theme.palette.grey[400],
    marginLeft: 1,
  },
  messageContent: {
    marginLeft: 5,
    whiteSpace: "pre-wrap",
  },
  messageImageContent: {
    borderRadius: 4,
  },
  messageAudioContent: {
    maxWidth: "100%",
  },
  messageVideoContent: {
    maxWidth: "75%",
    borderRadius: 4,
  },
  deliveryFailure: {
    marginLeft: 5,
    color: (theme) => theme.palette.error.main,
  },
  templateMessageText: {
    display: "inline",
    alignItems: "baseline",
  },
  hiddenMessageText: {
    fontStyle: "italic",
    color: "GrayText",
  },
  reactionContainer: {
    marginTop: 1,
    marginLeft: 5,
    display: "flex",
    flexDirection: "row",
    gap: 1,
  },
  reactionElement: {
    borderRadius: 1000,
    border: (theme) => `1px solid ${alpha(theme.palette.grey[500], 0.3)}`,
    display: "flex",
    flexDirection: "row",
    px: 1,
  },
});

export default ChatMessageEntry;
